import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { cityHeadService, type CityHead, type CreateCityHeadRequest, type UpdateCityHeadRequest } from '../services';
import { cityService, type City } from '../services/hubService';

const defaultFormState: CreateCityHeadRequest = {
  employeeId: '',
  name: '',
  phoneNumber: '',
  emailId: '',
  city: '',
};

const CityHeadManagement: React.FC = () => {
  const [cityHeads, setCityHeads] = useState<CityHead[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCityHead, setSelectedCityHead] = useState<CityHead | null>(null);
  const [formState, setFormState] = useState<CreateCityHeadRequest>(defaultFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CreateCityHeadRequest, string>>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadCities = async () => {
    try {
      const cityData = await cityService.getOperationalCities();
      setCities(cityData || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadCityHeads = async () => {
    setLoading(true);
    try {
      const response = await cityHeadService.getCityHeads();
      setCityHeads(response.data || []);
    } catch (error) {
      console.error('Error loading city heads:', error);
      setSnackbar({ open: true, message: 'Failed to load city head list.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCityHeads();
    loadCities();
  }, []);

  const handleOpenDialog = (cityHead?: CityHead) => {
    if (cityHead) {
      setSelectedCityHead(cityHead);
      setFormState({
        employeeId: cityHead.employeeId,
        name: cityHead.name,
        phoneNumber: cityHead.phoneNumber,
        emailId: cityHead.emailId,
        city: cityHead.city,
      });
      setIsEditing(true);
    } else {
      setSelectedCityHead(null);
      setFormState(defaultFormState);
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCityHead(null);
    setFormState(defaultFormState);
    setIsEditing(false);
  };

  const handleFormChange = (field: keyof CreateCityHeadRequest, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors: Partial<Record<keyof CreateCityHeadRequest, string>> = {};

    if (!formState.employeeId.trim()) {
      errors.employeeId = 'Employee ID is required.';
    }
    if (!formState.name.trim()) {
      errors.name = 'Name is required.';
    } else if (formState.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters.';
    }
    if (!formState.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required.';
    } else if (!/^[0-9]{10}$/.test(formState.phoneNumber.trim())) {
      errors.phoneNumber = 'Phone number must be a valid 10-digit number.';
    }
    if (!formState.emailId.trim()) {
      errors.emailId = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.emailId.trim())) {
      errors.emailId = 'Invalid email format.';
    }
    if (!formState.city.trim()) {
      errors.city = 'City is required.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    if (!validateForm()) {
      setSubmitLoading(false);
      return;
    }

    try {
      if (isEditing && selectedCityHead) {
        const updatePayload: UpdateCityHeadRequest = {
          employeeId: formState.employeeId,
          name: formState.name,
          phoneNumber: formState.phoneNumber,
          emailId: formState.emailId,
          city: formState.city,
        };
        await cityHeadService.updateCityHead(selectedCityHead.id, updatePayload);
        setSnackbar({ open: true, message: 'City Head updated successfully.', severity: 'success' });
      } else {
        await cityHeadService.createCityHead(formState);
        setSnackbar({ open: true, message: 'City Head created successfully.', severity: 'success' });
      }
      handleCloseDialog();
      await loadCityHeads();
    } catch (error: any) {
      console.error('Error saving city head:', error);
      const message = error?.response?.data?.message || 'Failed to save city head.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (cityHead: CityHead) => {
    if (!window.confirm(`Delete City Head ${cityHead.name}?`)) {
      return;
    }
    try {
      await cityHeadService.deleteCityHead(cityHead.id);
      setSnackbar({ open: true, message: 'City Head deleted successfully.', severity: 'success' });
      await loadCityHeads();
    } catch (error) {
      console.error('Error deleting city head:', error);
      setSnackbar({ open: true, message: 'Failed to delete city head.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            City Head Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create, edit, and manage City Head assignments across cities.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add City Head
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cityHeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No City Heads found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    cityHeads.map((cityHead) => (
                      <TableRow key={cityHead.id} sx={{ '& > td': { py: 1 } }}>
                        <TableCell>{cityHead.employeeId}</TableCell>
                        <TableCell>{cityHead.name}</TableCell>
                        <TableCell>{cityHead.phoneNumber}</TableCell>
                        <TableCell>{cityHead.emailId}</TableCell>
                        <TableCell>{cityHead.city}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton size="small" onClick={() => handleOpenDialog(cityHead)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(cityHead)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit City Head' : 'Add City Head'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Employee ID"
              value={formState.employeeId}
              onChange={(event) => handleFormChange('employeeId', event.target.value)}
              fullWidth
              error={!!formErrors.employeeId}
              helperText={formErrors.employeeId}
            />
            <TextField
              label="Name"
              value={formState.name}
              onChange={(event) => handleFormChange('name', event.target.value)}
              fullWidth
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              label="Phone Number"
              value={formState.phoneNumber}
              onChange={(event) => handleFormChange('phoneNumber', event.target.value)}
              fullWidth
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber}
            />
            <TextField
              label="Email"
              value={formState.emailId}
              onChange={(event) => handleFormChange('emailId', event.target.value)}
              fullWidth
              error={!!formErrors.emailId}
              helperText={formErrors.emailId}
            />
            <TextField
              select
              label="City"
              value={formState.city}
              onChange={(event) => handleFormChange('city', event.target.value)}
              fullWidth
              error={!!formErrors.city}
              helperText={formErrors.city}
            >
              <MenuItem value="">
                <em>Select a city</em>
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.name}>
                  {city.displayName || city.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button disabled={submitLoading} variant="contained" onClick={handleSubmit}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CityHeadManagement;
